import type { FirebaseOptions } from 'firebase/app'
import { initializeApp } from 'firebase/app'
import type { User } from 'firebase/auth'
import {
  connectAuthEmulator,
  getAuth,
  onAuthStateChanged,
  type Auth,
} from 'firebase/auth'
import {
  connectFirestoreEmulator,
  initializeFirestore,
  type Firestore,
} from 'firebase/firestore'
import {
  connectFunctionsEmulator,
  getFunctions,
  type Functions,
} from 'firebase/functions'
import {
  connectStorageEmulator,
  getDownloadURL,
  getStorage,
  ref,
  type FirebaseStorage,
} from 'firebase/storage'
import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  when,
} from 'mobx'
import { UnsubscribeManager } from '../util/UnsubscribeManager'
import { BreakoutUser } from './BreakoutUser'
import { GlobalAuthorStore } from '../stores/GlobalAuthorStore'
import { UserStore } from '../stores/UserStore'
import { DateTime } from 'luxon'
import { browserLocalPersistence, setPersistence } from 'firebase/auth'
import type { SlideDeckMaterial } from './SlideDeckMaterial'
import {
  getAnalytics,
  logEvent,
  setUserId,
  type Analytics,
} from 'firebase/analytics'
import { decodeJwt } from 'jose'
import * as Sentry from '@sentry/core'
import { EventEmitter } from '../util/EventEmitter'
import type { StreamSubscriptionActions } from 'tricklejs/dist/stream_subscription'
import { getSettingsFeatureFlags } from '../firestore/SettingsFeatureFlags'
import { SettingsFeatureFlags } from './SettingsFeatureFlags'
import { getSettingsFeatureFlagsBeta } from '../firestore/SettingsFeatureFlagsBeta'
import { getSettingsPricing } from '../firestore/SettingsPricing'
import { SettingsPricing } from './SettingsPricing'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from './SettingsSectionPassPricing'
import { fetchCSVFieldLabels } from '../firestore/CSVHeaders/index'
import { httpsCallable } from 'firebase/functions'
import { Section } from './Section'
import { PublicUser } from './PublicUser'
import type { CheckIsValidInvitationResult } from '../types'
import type {
  GetSlideDeckForAssignmentResult,
  SlideDeckInclude,
} from '../types/slideDeckAccess'
import {
  shouldUseV2SlideDeckRead,
  type SlideDeckReadSource,
} from '../cubits/slideDeckReadSource'
import { RubricTypeCubit } from '../cubits/RubricTypeCubit'
import { SettingsKillSwitch } from './SettingsKillSwitch'
import { getSettingsKillSwitch } from '../firestore/SettingsKillSwitch'
import { SettingsDeadAirDetector } from './SettingsDeadAirDetector'
import { getSettingsDeadAirDetector } from '../firestore/SettingsDeadAirDetector'
import { SettingsSoundcheck } from './SettingsSoundcheck'
import { getSettingsSoundcheck } from '../firestore/SettingsSoundcheck'
import { FirebaseRepositoryUtils } from './FirebaseRepositoryUtils'
import {
  resolveLocale,
  supportedLocales,
  type SupportedLocale,
} from '../util/locale'

// The Adonis server API surface the cubits call. `apps/web` owns the Tuyau
// client and injects it at build time as `repository.api`, so a cubit can do
// `repository.api.v1.slideDecks.show(...)`.
//
// This is a hand-written facade, NOT the server's generated Tuyau types on
// purpose: importing those would make firebase-repository depend on apps/server,
// which already depends on firebase-repository — a build cycle. The shapes here
// mirror the Tuyau call shape (params + query), so injecting the real client is
// a structural match with no cast. Extend this interface as cubits reach for
// more endpoints.
export interface RepositoryApiClient {
  v1: {
    slideDecks: {
      show(args: {
        params: { id: string }
        query: {
          sectionId?: string
          assignmentId?: string
          roomId?: string
          include: SlideDeckInclude[]
        }
      }): Promise<GetSlideDeckForAssignmentResult>
    }
  }
}

export type BuildOptions = {
  environment: string
  firebase: FirebaseOptions
  useLocalPersistence?: boolean
  disableAnalytics?: boolean
  inBeta?: boolean
  api?: RepositoryApiClient
  // The global slide-deck read source (v1 cloud function vs v2 Adonis). Read
  // once from sessionStorage at app boot; see apps/web getSlideDeckReadSource.
  slideDeckReadSource?: SlideDeckReadSource
}

export type FirebaseRepositoryEvents = {
  logout: []
}

// The result of a single client↔server clock-offset measurement: a usable
// reading ('ok'), a round trip too slow to trust ('slow'), or a token we could
// not read an `iat` from ('unusable').
type ClockOffsetSample =
  | { kind: 'ok'; offsetMs: number; rttMs: number }
  | { kind: 'slow'; rttMs: number }
  | { kind: 'unusable' }

// The token exchange with Google's secure token service is a sub-second request.
// A round trip much larger than that means the midpoint is a poor proxy for
// "server time now" (asymmetric-latency error grows with the round trip), so we
// discard such a sample rather than commit a low-quality offset.
const MAX_CLOCK_OFFSET_RTT_MS = 10_000

// Compute a single client↔server offset sample from an ID token and the timing of
// the request that fetched it. The token's `iat` (issued-at) claim is server time
// in seconds at the moment Google's auth servers signed it, so comparing it to the
// local clock at the round-trip midpoint yields the offset (NTP-style). Never
// throws: a missing `iat` or unparseable token is reported as 'unusable'.
function measureClockOffset(
  token: string,
  sentAtMs: number,
  receivedAtMs: number
): ClockOffsetSample {
  try {
    const { iat } = decodeJwt(token)
    if (!iat) return { kind: 'unusable' }

    const rttMs = receivedAtMs - sentAtMs
    if (rttMs > MAX_CLOCK_OFFSET_RTT_MS) {
      return { kind: 'slow', rttMs }
    }

    const offsetMs = iat * 1000 - (sentAtMs + receivedAtMs) / 2
    return { kind: 'ok', offsetMs, rttMs }
  } catch {
    return { kind: 'unusable' }
  }
}

// Stable per-tab identity, attached to every analytics event as `tab_id` /
// `tab_age_ms`. sessionStorage is scoped to one tab and survives reloads within
// it, so a tab stuck re-booting keeps a single id and its age keeps growing
// across reloads — that is how we count concurrent open tabs (distinct tab_id)
// and tie a burst of reloads to one long-lived tab. Cached at module scope
// (one tab = one JS context) so we touch sessionStorage once.
let cachedTabId: string | undefined
let cachedTabCreatedAt: number | undefined
function ensureTab(): { id: string; createdAt: number } {
  if (cachedTabId !== undefined && cachedTabCreatedAt !== undefined) {
    return { id: cachedTabId, createdAt: cachedTabCreatedAt }
  }
  let id: string
  let createdAt: number
  try {
    const existingId = sessionStorage.getItem('breakoutTabId')
    const existingCreated = Number(
      sessionStorage.getItem('breakoutTabCreatedAt')
    )
    if (existingId && Number.isFinite(existingCreated)) {
      id = existingId
      createdAt = existingCreated
    } else {
      id = crypto.randomUUID().slice(0, 8)
      createdAt = Date.now()
      sessionStorage.setItem('breakoutTabId', id)
      sessionStorage.setItem('breakoutTabCreatedAt', String(createdAt))
    }
  } catch {
    // sessionStorage unavailable (tests / hardened browser); fall back to an
    // in-memory identity so events still carry a per-context tab id and age.
    id = crypto.randomUUID().slice(0, 8)
    createdAt = Date.now()
  }
  cachedTabId = id
  cachedTabCreatedAt = createdAt
  return { id, createdAt }
}

// TODO: move to stores
// Single class housing all Firebase services
export class FirebaseRepository {
  firestore: Firestore
  auth: Auth
  storage: FirebaseStorage
  functions: Functions
  analytics?: Analytics
  initialized = false
  inBeta = false
  environment: string
  databaseId?: string

  // The Adonis server client, injected by apps/web (see BuildOptions).
  // Undefined here in the repo package and in tests, so the cubits fall back to
  // the cloud function unless it's set.
  api?: RepositoryApiClient

  // Global slide-deck read source, set once at build from the per-tab
  // sessionStorage override. Undefined = the default v1 cloud function.
  slideDeckReadSource?: SlideDeckReadSource

  // The single decision point for the v1/v2 slide-deck read: v2 only when the
  // override is 'v2' AND the api client is injected. Cubits read this instead
  // of branching on the source themselves.
  get readsSlideDeckFromV2(): boolean {
    return shouldUseV2SlideDeckRead(this.slideDeckReadSource) && !!this.api
  }

  // This is a testing only flag
  _testCrashApp = observable.box(false)

  currentUser: User | null = null
  breakoutUser: BreakoutUser | null = null

  supportedLocales: readonly SupportedLocale[] = supportedLocales
  locale: SupportedLocale = 'en'

  _authToken: string | null = null
  _authTokenRefreshTimeout: NodeJS.Timeout | undefined = undefined
  _featureFlagStream: StreamSubscriptionActions | undefined = undefined
  _killSwitchStream: StreamSubscriptionActions | undefined = undefined
  _deadAirSettingsStream: StreamSubscriptionActions | undefined = undefined
  _soundcheckSettingsStream: StreamSubscriptionActions | undefined = undefined
  _featureFlags: SettingsFeatureFlags
  _killSwitch: SettingsKillSwitch
  _deadAirSettings: SettingsDeadAirDetector
  _soundcheckSettings: SettingsSoundcheck
  _pricing: SettingsPricing
  _pricingStream: StreamSubscriptionActions | undefined = undefined
  _sectionPassPricingStream: StreamSubscriptionActions | undefined = undefined
  _sectionPassPricing: SettingsSectionPassPricing

  rubricTypes: RubricTypeCubit

  // with observables, relying on time is a bit tricky
  // so we have a separate observable for the current minute
  // which is updated every minute
  // A minute, because we don't need to be more granular than that
  currentMinute: DateTime = DateTime.now()
  _currentMinuteInterval: NodeJS.Timeout | null = null

  // Client↔server clock offset (serverClock - deviceClock, in ms), measured from
  // the ID token's `iat` claim when the auth flow fetches a fresh token. Defaults
  // to 0, so consumers behave exactly as before until (and unless) it is measured.
  // Read in the slide timer tick — not a render path — so not observable.
  clockOffsetMs = 0

  // Whether the clock-offset measurement has settled (applied, rejected, or found
  // unnecessary). It goes false only while a measurement is in flight — which, for
  // a large offset, spans a second token round-trip. Until it settles the slide
  // timer must not act on an unverified elapsed reading: a device clock that is far
  // ahead would otherwise draw a bogus countdown and, worse, end a fresh slide
  // outright before the correction lands. Defaults to true so consumers that never
  // measure (and the moment before we start) behave exactly as before.
  clockOffsetSettled = true

  // Whether the clock offset has been measured at least once. Distinct from
  // clockOffsetSettled (which defaults true): this defaults false so the slide
  // timer can stay in its loading state on rejoin until the first measurement
  // lands, instead of flashing a countdown computed from an unverified device
  // clock. Set true when recordClockOffsetFromToken completes (any outcome).
  clockOffsetMeasured = false

  // Whether serverNow() is backed by an applied offset. A measurement can finish
  // without one (slow round trip, unreadable token, samples that disagree), and
  // then serverNow() is still the raw device clock. Decisions that act on an
  // elapsed reading (ending a slide, restarting an abandoned room's countdown)
  // require this, not just clockOffsetMeasured.
  clockOffsetVerified = false

  utils: FirebaseRepositoryUtils = new FirebaseRepositoryUtils(this)

  /**
   * prevent user from mounting the AuthedEntrypoint
   * currently used to block re-direct when user is
   * linking 2nd auth method on the loginPAge
   */
  entrypointLocked: boolean = false

  globalAuthorStore: GlobalAuthorStore = new GlobalAuthorStore(this)
  userStore: UserStore = new UserStore(this)

  numberStore = observable.map<string, number>({})

  unsubscribers = new UnsubscribeManager()
  events = new EventEmitter<FirebaseRepositoryEvents>()

  hasZodError: boolean = false

  static build(options: BuildOptions) {
    const app = initializeApp(options.firebase)
    const auth = getAuth(app)
    const databaseId = localStorage.getItem('firestore-databaseId')
    let firestore: Firestore
    if (databaseId) {
      firestore = initializeFirestore(
        app,
        { ignoreUndefinedProperties: true },
        databaseId
      )
    } else {
      firestore = initializeFirestore(app, { ignoreUndefinedProperties: true })
    }
    const storage = getStorage(app)
    const functions = getFunctions(app)
    const analytics = options.disableAnalytics ? undefined : getAnalytics(app)

    if (options.useLocalPersistence) {
      setPersistence(auth, browserLocalPersistence)
    }

    const repository = new FirebaseRepository(
      firestore,
      auth,
      storage,
      functions,
      options.environment,
      analytics
    )

    if (options.inBeta) repository.inBeta = true
    if (databaseId) repository.databaseId = databaseId
    if (options.api) repository.api = options.api
    if (options.slideDeckReadSource)
      repository.slideDeckReadSource = options.slideDeckReadSource

    return repository
  }

  constructor(
    firestore: Firestore,
    auth: Auth,
    storage: FirebaseStorage,
    functions: Functions,
    environment: string,
    analytics?: Analytics
  ) {
    this.firestore = firestore
    this.auth = auth
    this.storage = storage
    this.functions = functions
    this.analytics = analytics
    this.environment = environment

    this.breakoutUser = null

    this._featureFlags = SettingsFeatureFlags.empty(this)
    this._pricing = SettingsPricing.empty(this)
    this._killSwitch = SettingsKillSwitch.empty(this)
    this._deadAirSettings = SettingsDeadAirDetector.empty(this)
    this._soundcheckSettings = SettingsSoundcheck.empty(this)
    this._sectionPassPricing = SettingsSectionPassPricing.empty(this)

    this.rubricTypes = new RubricTypeCubit(this)

    makeObservable(this, {
      // observables
      _authToken: observable,
      entrypointLocked: observable,
      currentUser: observable,
      initialized: observable,
      currentMinute: observable,
      breakoutUser: observable,
      locale: observable,
      hasZodError: observable,

      // actions
      update: action,
      finalizeInitialization: action,
      updateCurrentMinute: action,
      lockEntrypoint: action,
      unlockEntrypoint: action,
      setLocale: action,
      testCrashApp: action,
      triggerOnZodError: action,

      // computed
      uid: computed,
      isAuthenticated: computed,
      authToken: computed,
      shouldTestCrashApp: computed,
    })
  }

  initialize() {
    this.startMinuteTicker()
    this.unsubscribers.add(
      onAuthStateChanged(this.auth, async (user) => {
        // Force a token refresh before setting the user. When onAuthStateChanged
        // fires, Firebase Auth knows about the user but the auth token may not
        // yet be propagated to Firestore. Setting the user immediately triggers
        // MobX reactions that start Firestore streams (e.g. getUserProfile,
        // getActiveGradeSyncJobs, getPeerReviewNotesForRevieweeInRoom), which
        // fail with "Missing or insufficient permissions" because Firestore
        // security rules can't validate the not-yet-propagated token.
        // By awaiting getIdToken(true) first, we ensure the token is valid and
        // available to Firestore before any streams are started.
        // See: CASES-REACT-2PY, CASES-REACT-2Y3, CASES-REACT-283
        if (user) {
          // This forced token fetch is also our clock-offset sample: the fresh
          // token's `iat` is server time, so we read it here instead of paying
          // for a second request. See recordClockOffsetFromToken. It may take one
          // more sample (only if the offset looks implausibly large) to tell a
          // real clock skew from a stale token before trusting it.
          const sentAt = Date.now()
          const token = await user.getIdToken(true)
          // Fire-and-forget: the offset only feeds a non-render slide-timer tick,
          // so it must not delay update({ user }) (and the Firestore streams it
          // starts). The corroboration path can take a second token round-trip;
          // blocking on it would stall startup for exactly the wrong-clock users
          // this correction exists to help. Never throws, but guard anyway.
          void this.recordClockOffsetFromToken(token, sentAt, Date.now(), () =>
            user.getIdToken(true)
          ).catch(() => {})
        }
        this.update({ user })
        if (!this.initialized) this.finalizeInitialization()
      })
    )

    // fetch/refresh the auth token when currentUser changes
    this.unsubscribers.add(
      reaction(
        () => this.currentUser,
        async () => {
          this.refreshAuthToken()
        },
        { fireImmediately: true }
      )
    )

    return this
  }

  fetchCSVFieldLabels = async () => {
    return await fetchCSVFieldLabels(this)
  }

  lockEntrypoint = () => {
    this.entrypointLocked = true
  }

  unlockEntrypoint = () => {
    this.entrypointLocked = false
  }

  get inProduction() {
    return this.environment === 'production'
  }

  get inProductionOrBeta() {
    return this.environment === 'production' || this.environment === 'beta'
  }

  setLocale = (locale: string) => {
    this.locale = resolveLocale(locale)
  }

  onLogout(callback: () => void) {
    this.events.addListener('logout', callback)
  }

  offLogout(callback: () => void) {
    this.events.removeListener('logout', callback)
  }

  get featureFlags() {
    this.startFeatureFlagStreamIfNotRunning()
    return this._featureFlags
  }

  get killSwitch() {
    this.startKillSwitchStreamIfNotRunning()
    return this._killSwitch
  }

  get deadAirSettings() {
    this.startDeadAirSettingsStreamIfNotRunning()
    return this._deadAirSettings
  }

  get soundcheckSettings() {
    this.startSoundcheckSettingsStreamIfNotRunning()
    return this._soundcheckSettings
  }

  get pricing() {
    this.startPricingStreamIfNotRunning()
    return this._pricing
  }

  get sectionPassPricing() {
    this.startSectionPassPricingStreamIfNotRunning()
    return this._sectionPassPricing
  }

  startFeatureFlagStreamIfNotRunning() {
    if (this._featureFlagStream) return

    const stream = this.inBeta
      ? getSettingsFeatureFlagsBeta(this)
      : getSettingsFeatureFlags(this)
    this._featureFlagStream = stream.listen((model) => {
      // should feature flags fail to load, we don't want to block the app
      // use the defaults
      if (!model.data) {
        this._featureFlags.changeLoadingState(false)
        return
      }
      this._featureFlags.replaceModel(model)
    })
  }

  startPricingStreamIfNotRunning() {
    if (!this.uid) return
    if (this._pricingStream) return

    this._pricingStream = getSettingsPricing(this).listen((model) => {
      this._pricing.replaceModel(model)
    })
  }

  startSectionPassPricingStreamIfNotRunning() {
    if (this._sectionPassPricingStream) return

    this._sectionPassPricingStream = getSettingsSectionPassPricing(this).listen(
      (model) => {
        this._sectionPassPricing.replaceModel(model)
      }
    )
  }

  startKillSwitchStreamIfNotRunning() {
    if (this._killSwitchStream) return

    this._killSwitchStream = getSettingsKillSwitch(this).listen((model) => {
      this.killSwitch.replaceModel(model)
    })
  }

  startDeadAirSettingsStreamIfNotRunning() {
    if (this._deadAirSettingsStream) return

    this._deadAirSettingsStream = getSettingsDeadAirDetector(this).listen(
      (model) => {
        this._deadAirSettings.replaceModel(model)
      }
    )
  }

  startSoundcheckSettingsStreamIfNotRunning() {
    if (this._soundcheckSettingsStream) return

    this._soundcheckSettingsStream = getSettingsSoundcheck(this).listen(
      (model) => {
        this._soundcheckSettings.replaceModel(model)
      }
    )
  }

  logEvent(name: string, params?: Record<string, unknown>) {
    // Test tap, before the analytics guard rather than after it. Analytics is
    // constructed with `disableAnalytics: inE2E()`, so under Playwright this
    // method returns on the next line and every event the app emits is
    // unobservable — including the ones that are the only record of what the
    // dead-air detector decided. Nothing installs the tap outside a test; a
    // browser that has not had one written onto it takes the same two lines it
    // always did.
    const tap = (
      globalThis as {
        __breakoutLogEventTap?: (
          name: string,
          params?: Record<string, unknown>
        ) => void
      }
    ).__breakoutLogEventTap
    if (tap) {
      try {
        tap(name, params)
      } catch {
        // A broken tap is a broken test, not a broken room.
      }
    }
    if (this.analytics === undefined) return
    const tab = ensureTab()
    logEvent(this.analytics, name, {
      tab_id: tab.id,
      tab_age_ms: Date.now() - tab.createdAt,
      ...params,
    })
  }

  // Single entry point for the getSlideDeckForAssignment callable. Every caller
  // routes through here so there is one place to reason about (and instrument)
  // this call. `callsite` and `reason` are forwarded to the server and logged
  // there, so we can see exactly where and why each invocation originated while
  // we track down what drives the call volume.
  async fetchSlideDeckForAssignment(params: {
    sectionId?: string
    assignmentId?: string
    roomId?: string
    slideDeckId?: string
    include: SlideDeckInclude[]
    callsite: string
    reason: string
    // Optional diagnostics: the calling cubit instance id (to spot re-create
    // loops) and its in-memory room selection (may differ from the URL).
    instanceId?: string
    clientRoomStateId?: string
  }): Promise<GetSlideDeckForAssignmentResult> {
    // A v2 opt-in with no api client silently falls back to v1 — surface the
    // misconfiguration so a `-adonis` run isn't secretly re-testing v1.
    if (shouldUseV2SlideDeckRead(this.slideDeckReadSource) && !this.api) {
      console.warn(
        '[slide-deck] v2 read requested but repository.api is not injected; using v1'
      )
    }

    // v2 (Adonis/Postgres) when the override is on, the api client is injected,
    // and we have a deck id for the URL path; otherwise the v1 cloud function.
    // Both return the same GetSlideDeckForAssignmentResult shape.
    if (this.readsSlideDeckFromV2 && this.api && params.slideDeckId) {
      return this.api.v1.slideDecks.show({
        params: { id: params.slideDeckId },
        query: {
          ...(params.sectionId && { sectionId: params.sectionId }),
          ...(params.assignmentId && { assignmentId: params.assignmentId }),
          ...(params.roomId && { roomId: params.roomId }),
          include: params.include,
        },
      })
    }

    const callable = httpsCallable<
      {
        sectionId?: string
        assignmentId?: string
        roomId?: string
        slideDeckId?: string
        include: SlideDeckInclude[]
        callsite: string
        reason: string
        instanceId?: string
        clientRoomStateId?: string
        databaseId?: string
      },
      GetSlideDeckForAssignmentResult
    >(this.functions, 'getSlideDeckForAssignment')

    const { data } = await callable({
      ...params,
      ...(this.databaseId && { databaseId: this.databaseId }),
    })
    return data
  }

  setAnalyticsUserId(userId: string | null) {
    if (this.analytics === undefined) return
    setUserId(this.analytics, userId)
  }

  startMinuteTicker() {
    const firstTargetTime = DateTime.now().endOf('minute').plus({ seconds: 1 }) // The next time of the form XX:YY:01, but not less than 1 second (for stability reasons)
    const firstDelayMs = firstTargetTime.diffNow().toMillis()

    const regularIntervalMs = 60 * 1000

    setTimeout(() => {
      this.updateCurrentMinute()
      this._currentMinuteInterval = setInterval(() => {
        this.updateCurrentMinute()
      }, regularIntervalMs)
    }, firstDelayMs)
  }

  updateCurrentMinute() {
    this.currentMinute = DateTime.now()
  }

  // The device wall clock corrected onto the server timeline. Elapsed-time math
  // that subtracts a serverTimestamp() value (e.g. the slide timer) must use this
  // instead of Date.now() so a wrong device clock does not skew the result.
  serverNow() {
    return Date.now() + this.clockOffsetMs
  }

  // An offset this large is more likely a stale token than a real clock skew, so
  // we don't trust a single sample of it — see recordClockOffsetFromToken.
  static readonly MAX_TRUSTED_CLOCK_OFFSET_MS = 15 * 60 * 1000

  // Two independent samples must agree within this window to corroborate a large
  // offset. Generous enough to absorb `iat`'s one-second granularity and RTT jitter.
  static readonly CLOCK_OFFSET_AGREEMENT_MS = 5_000

  // Derive the client↔server clock offset from a freshly-minted ID token, without
  // paying for a dedicated request: the auth flow already force-fetches a token,
  // so we piggyback on it and pass its round-trip timing here.
  //
  // A small offset is trusted immediately (the common case). A large offset is
  // ambiguous — it could be a genuinely wrong device clock (which we *want* to
  // correct) or a stale token (which we must reject), and one sample can't tell
  // them apart. So we take a second independent sample via `refetchToken` and
  // apply the offset only if the two corroborate: a real skew reproduces, a stale
  // token collapses to ~0. On any failure the offset is left unchanged (defaults
  // to 0, i.e. today's behavior).
  async recordClockOffsetFromToken(
    token: string,
    sentAtMs: number,
    receivedAtMs: number,
    refetchToken?: () => Promise<string>
  ) {
    const apply = (offsetMs: number, rttMs: number) => {
      this.clockOffsetMs = offsetMs
      this.clockOffsetVerified = true
      this.logEvent('slide_timer_clock_offset', {
        offset_ms: Math.round(offsetMs),
        abs_offset_ms: Math.round(Math.abs(offsetMs)),
        rtt_ms: Math.round(rttMs),
      })
    }
    const reject = (offsetMs: number, reason: string) => {
      this.logEvent('slide_timer_clock_offset', {
        offset_ms: Math.round(offsetMs),
        abs_offset_ms: Math.round(Math.abs(offsetMs)),
        rejected: true,
        reason,
      })
    }

    // Mark the measurement in flight until it settles. This is synchronous for
    // every fast path (no await before their returns), so only the large-offset
    // corroboration — which awaits a second token round-trip — actually holds the
    // flag false, exactly the window in which the slide timer must not trust the
    // still-uncorrected clock. The finally guarantees it is always cleared.
    this.clockOffsetSettled = false
    try {
      const first = measureClockOffset(token, sentAtMs, receivedAtMs)
      if (first.kind === 'unusable') {
        this.logEvent('slide_timer_clock_offset', {
          rejected: true,
          reason: 'unusable',
        })
        return
      }
      if (first.kind === 'slow') {
        // Round trip too slow to trust the midpoint: leave the prior/default
        // offset in place. Still logged so the field can size how often we skip.
        this.logEvent('slide_timer_clock_offset', {
          rtt_ms: Math.round(first.rttMs),
          rejected: true,
          reason: 'rtt',
        })
        return
      }

      if (
        Math.abs(first.offsetMs) <=
        FirebaseRepository.MAX_TRUSTED_CLOCK_OFFSET_MS
      ) {
        apply(first.offsetMs, first.rttMs)
        return
      }

      // Large offset: corroborate with a second independent sample before
      // trusting it. Without a way to re-fetch we can't corroborate, so we reject.
      if (!refetchToken) {
        reject(first.offsetMs, 'uncorroborated')
        return
      }

      let second: ClockOffsetSample
      try {
        const retrySentAt = Date.now()
        const retryToken = await refetchToken()
        second = measureClockOffset(retryToken, retrySentAt, Date.now())
      } catch {
        second = { kind: 'unusable' }
      }

      if (second.kind !== 'ok') {
        reject(first.offsetMs, 'uncorroborated')
        return
      }

      if (
        Math.abs(first.offsetMs - second.offsetMs) >
        FirebaseRepository.CLOCK_OFFSET_AGREEMENT_MS
      ) {
        // The two samples disagree — the large first reading was a glitch/stale
        // token, not a real skew. Keep the prior/default offset.
        reject(second.offsetMs, 'inconsistent')
        return
      }

      // Corroborated: a genuinely skewed device clock. Use the second (more
      // recent) sample.
      apply(second.offsetMs, second.rttMs)
    } finally {
      this.clockOffsetSettled = true
      this.clockOffsetMeasured = true
    }
  }

  finalizeInitialization() {
    this.initialized = true
  }

  dispose() {
    this._currentMinuteInterval && clearInterval(this._currentMinuteInterval)
    this.unsubscribers.dispose()
  }

  connectToEmulator(host?: string) {
    const emulatorHost = host || 'localhost'

    // skip if we're already using the emulator in the browser
    // hot reload can cause this to be called multiple times
    if (this.auth.emulatorConfig) return

    connectAuthEmulator(this.auth, `http://${emulatorHost}:9099`, {
      disableWarnings: true,
    })
    connectFirestoreEmulator(this.firestore, emulatorHost, 8080)
    connectStorageEmulator(this.storage, emulatorHost, 9199)
    connectFunctionsEmulator(this.functions, emulatorHost, 5001)
  }

  update(data: { user: User | null }) {
    if (data.user !== undefined) {
      this.currentUser = data.user

      if (data.user) {
        this.setAnalyticsUserId(data.user.uid)
        this.breakoutUser = new BreakoutUser(this, data.user.uid)
        this.breakoutUser.initialize()

        // we do this on login, its not in the initialize because it is only safe to call
        // if user is currently authed as the user represented by the class
        // that is always true here on login
        this.breakoutUser.syncProfileEmailToAuthValue()

        Sentry.setUser({ id: data.user.uid })
      } else {
        this.logout()
        this.setAnalyticsUserId(null)
        this.breakoutUser = new BreakoutUser(this, '')
        Sentry.setUser(null)
      }
    }
  }

  get authToken() {
    // check if the token is expired as a fail safe in case the token is not refreshed
    if (this._authToken) {
      const { exp } = decodeJwt(this._authToken)
      // should only occur if the 10 minute refresh failed
      const nowMinusFiveMinutes = DateTime.now()
        .minus({ minutes: 5 })
        .toSeconds()
      if (exp && exp < nowMinusFiveMinutes) this.refreshAuthToken(true)
    }
    return this._authToken
  }

  get uid() {
    return this.breakoutUser?.uid || ''
  }

  get isAuthenticated() {
    return this.currentUser !== null
  }

  logout() {
    // we're logging out, dispose of the user
    this.breakoutUser?.dispose()
    // clear the user store and cancel subscription
    this.userStore.clear()
    this.globalAuthorStore.clear()

    this.events.emit('logout')

    if (this._featureFlagStream) {
      this._featureFlagStream.cancel()
      this._featureFlagStream = undefined
    }

    if (this._pricingStream) {
      this._pricingStream?.cancel()
      this._pricingStream = undefined
    }

    if (this._sectionPassPricingStream) {
      this._sectionPassPricingStream.cancel()
      this._sectionPassPricingStream = undefined
    }

    if (this._killSwitchStream) {
      this._killSwitchStream.cancel()
      this._killSwitchStream = undefined
    }

    // Left running, this one errors: `settings/dead_air_detector` is gated on
    // isSignedIn(), so the live listener takes a permission-denied the moment
    // auth clears, and its stream reports it to Sentry. Clearing the handle
    // also lets a re-login in the same tab start the stream again — the SDK
    // does not revive a listener it closed.
    if (this._deadAirSettingsStream) {
      this._deadAirSettingsStream.cancel()
      this._deadAirSettingsStream = undefined
    }
  }

  getPdfMaterialDownloadUrl = async (
    slideDeckId: string,
    material: SlideDeckMaterial
  ) => {
    const pdfRef = ref(
      this.storage,
      `slide_deck/${slideDeckId}/material/${material.id}.pdf`
    )
    const pdfUrl = await getDownloadURL(pdfRef)

    return pdfUrl
  }

  refreshAuthToken = async (forceRefresh = false) => {
    clearTimeout(this._authTokenRefreshTimeout)
    // if we're not authenticated, clear the token
    if (!this.currentUser) {
      this._authToken = null
      return
    }

    try {
      // eslint-disable-next-line no-console
      if (process.env.NODE_ENV !== 'test') console.log('Fetching new AuthToken')

      const token = await this.currentUser.getIdToken(forceRefresh)

      // Decode the token to get the expiration time
      const { exp } = decodeJwt(token)
      const nowSeconds = DateTime.now().toSeconds()

      // Set the token if it's not expired
      if (exp && exp > nowSeconds) this._authToken = token

      // force refresh 10 mins before expiration
      const refreshAt = Math.max(exp ? exp - nowSeconds - 600 : 0, 0)
      this._authTokenRefreshTimeout = setTimeout(
        () => this.refreshAuthToken(true),
        refreshAt * 1000
      )
    } catch (e) {
      // if we fail to get the token, try again in 10 seconds
      this._authTokenRefreshTimeout = setTimeout(
        () => this.refreshAuthToken(true),
        10 * 1000
      )
    }
  }

  debug(
    category: string,
    message: string,
    // params to pass to analytics
    { params }: { params?: Record<string, unknown> } = {}
  ) {
    // do not spam the console in production
    if (!this.inProduction) {
      // eslint-disable-next-line no-console
      console.debug(`[${category}] ${message}`, params)
    }
    // Push to sentry so that we can pick it up there
    Sentry.addBreadcrumb({
      type: 'debug',
      message: message,
      category: category,
    })
    // Log to analytics as well
    const category_safe = category.replace(/\./g, '_').toLocaleLowerCase()
    this.logEvent(`debug_${category_safe}`, {
      message,
      ...params,
    })
  }

  async checkIsValidInvitation(invitationId: string) {
    const checkInvite = httpsCallable<
      { invitationId: string },
      CheckIsValidInvitationResult
    >(this.functions, 'checkIsValidInvitation')

    const { data } = await checkInvite({ invitationId })

    // if it's a section invitation, replace the data with the correct models
    if (data.isValid && data.invitationType === 'section') {
      // The backend returns timestamps as objects with _seconds field
      // and we need to convert them to Date objects
      const cleaned = { ...data.section }
      for (const key in cleaned) {
        if (key === 'sectionStartDate' || key === 'updatedAt') {
          const value = cleaned[key] as { _seconds?: number }
          const seconds = value._seconds
          if (seconds !== undefined) {
            cleaned[key] = new Date(seconds * 1000)
          }
        }
      }
      return {
        ...data,
        section: Section.empty(this).replaceData(cleaned),
        instructor: PublicUser.empty(this).replaceData(data.instructor),
      }
    }

    return data
  }

  // convenience function to ensure the required user data is loaded
  async waitForBreakoutUser(): Promise<BreakoutUser> {
    await when(
      () => {
        if (!this.breakoutUser) return false
        return this.breakoutUser.isLoaded
      },
      { timeout: 10_000 }
    )

    return this.breakoutUser!
  }

  get shouldTestCrashApp(): boolean {
    // NEVER allow crashing in production
    if (this.inProduction) return false

    return this._testCrashApp.get()
  }

  testCrashApp(): void {
    // NEVER allow crashing in production
    if (this.inProduction) return
    this._testCrashApp.set(true)
  }

  triggerOnZodError(): void {
    this.hasZodError = true
  }
}
